
import React, { useEffect, useState, useRef } from 'react';
import { Sun, Play, Pause, RefreshCw } from 'lucide-react';
import { decodeAudioData, base64ToUint8Array } from '../utils/audioUtils';
import { useTheme } from '../contexts/ThemeContext';
import { BaseModal } from './BaseModal';

interface Props {
  script: string;
  audioData: string;
  onClose: () => void;
}

export const MorningManifestoModal: React.FC<Props> = ({ script, audioData, onClose }) => {
  const [isPlaying, setIsPlaying] = useState(false);
  const [isPlayingComplete, setIsPlayingComplete] = useState(false);
  const audioContextRef = useRef<AudioContext | null>(null);
  const sourceRef = useRef<AudioBufferSourceNode | null>(null);
  const startTimeRef = useRef<number>(0);
  const pausedTimeRef = useRef<number>(0);
  const { theme } = useTheme();
  const fStyle = theme.colors.features.manifesto;

  useEffect(() => {
    return () => {
        stopAudio();
    };
  }, []);

  // Initialize and play automatically on mount
  useEffect(() => {
      if (audioData) {
          playAudio(0);
      }
  }, [audioData]);

  const playAudio = async (offset = 0) => {
    try {
        if (!audioContextRef.current) {
            audioContextRef.current = new (window.AudioContext || (window as any).webkitAudioContext)();
        }
        const ctx = audioContextRef.current;
        
        // Decode
        const uint8 = base64ToUint8Array(audioData);
        const buffer = await decodeAudioData(uint8, ctx, 24000, 1);

        // Create Source
        const source = ctx.createBufferSource();
        source.buffer = buffer;
        source.connect(ctx.destination);
        source.onended = () => {
            setIsPlaying(false);
            setIsPlayingComplete(true);
            pausedTimeRef.current = 0;
        };

        source.start(0, offset);
        sourceRef.current = source;
        startTimeRef.current = ctx.currentTime - offset;
        setIsPlaying(true);
        setIsPlayingComplete(false);

    } catch (e) {
        console.error("Failed to play audio", e);
    }
  };

  const stopAudio = () => {
      if (sourceRef.current) {
          try { sourceRef.current.stop(); } catch(e) {}
          sourceRef.current = null;
      }
      setIsPlaying(false);
  };

  const handleTogglePlay = () => {
      if (isPlaying) {
          if (audioContextRef.current) {
              pausedTimeRef.current = audioContextRef.current.currentTime - startTimeRef.current;
          }
          stopAudio();
      } else {
          playAudio(pausedTimeRef.current);
      }
  };

  const handleReplay = () => {
      stopAudio();
      pausedTimeRef.current = 0;
      playAudio(0);
  };

  return (
    <BaseModal
        onClose={onClose}
        title="Morning Manifesto"
        icon={<Sun size={16} />}
        headerColor={fStyle}
        maxWidth="max-w-lg"
    >
        <div className={`p-8 flex flex-col items-center justify-center space-y-8 bg-gradient-to-b ${theme.mode === 'dark' ? 'from-slate-800 to-slate-900' : 'from-orange-50 to-white'}`}>
            
            {/* Visualizer Circle */}
            <div className={`relative w-40 h-40 flex items-center justify-center`}>
                <div className={`absolute inset-0 rounded-full opacity-20 ${isPlaying ? 'animate-ping' : ''} ${fStyle.bg}`}></div>
                <div className={`absolute inset-0 m-4 rounded-full opacity-20 ${isPlaying ? 'animate-pulse' : ''} ${fStyle.bg}`}></div>
                <div className={`relative z-10 w-24 h-24 rounded-full shadow-xl flex items-center justify-center bg-gradient-to-br from-orange-400 to-red-500`}>
                    <button 
                        onClick={isPlayingComplete ? handleReplay : handleTogglePlay}
                        className="text-white hover:scale-110 transition-transform"
                    >
                        {isPlaying ? <Pause size={32} fill="currentColor" /> : isPlayingComplete ? <RefreshCw size={32} /> : <Play size={32} fill="currentColor" className="ml-1" />}
                    </button>
                </div>
            </div>

            <div className="text-center space-y-4">
                <p className={`font-serif italic text-lg leading-relaxed max-w-md mx-auto ${theme.colors.text.secondary}`}>
                    "{script}"
                </p>
                <div className="text-xs uppercase tracking-widest opacity-50 font-bold">Your Daily Spark</div>
            </div>
        </div>
    </BaseModal>
  );
};
